import React, { useState, useEffect, useRef, useContext } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { osName, osVersion } from 'react-device-detect';
import io from 'socket.io-client';
import CryptoJS from 'crypto-js';

import Copybar from '../../images/copy.png';
import toMoney from '../../functions/toMoney';
import toLocaleCurrency from '../../functions/toLocaleCurrency';
import { computeDiscount } from '../../functions/paymentRules';
import retryAsync from '../../functions/retryAsync';
import AuthContext from '../../contexts/auth';
import { apiCharge } from '../../services/api';
import { generatePix, validatePix, cancelPix } from '../../services/pix';
import { getRuntimeConfig } from '../../services/runtimeConfig';

import './style.css';

const SOCKET_URL = getRuntimeConfig('REACT_APP_BACKEND_VIRTUS_URL');

const PIX_EXPIRATION = 3600; // segundos solicitados na geração (1h)

const normalize = value =>
  String(value && value.status ? value.status : value || '')
    .trim()
    .toUpperCase();

function PaymentModal({
  show,
  onClose,
  selected,
  cnpj,
  discountRules,
  onSelectCard,
  onPaid,
}) {
  const { user } = useContext(AuthContext);
  const token = JSON.parse(sessionStorage.getItem('@portal-cliente/token'));

  const [step, setStep] = useState('choose'); // choose | pix | success
  const [pixLoading, setPixLoading] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [pixError, setPixError] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [expired, setExpired] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [copied, setCopied] = useState(false);

  const timerRef = useRef(null);
  const pollRef = useRef(null);
  const socketRef = useRef(null);
  const paidRef = useRef(false);
  const txidRef = useRef(null);
  const idAppRef = useRef(null);

  const total = selected.reduce(
    (sum, item) => sum + toLocaleCurrency(item.price),
    0,
  );

  // Desconto por antecipação para PIX ('PX'). Sem regras/desconto, perTitleCents
  // fica zerado e o fluxo é idêntico ao atual.
  const discount = computeDiscount(discountRules, cnpj, selected, 'PX');
  const finalTotal = Math.max(0, total - (discount.totalDiscount || 0));
  const netValor = index => {
    const cents = (discount.perTitleCents && discount.perTitleCents[index]) || 0;
    return Math.max(0, toLocaleCurrency(selected[index].price) - cents / 100);
  };

  const clearTimers = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (pollRef.current) clearInterval(pollRef.current);
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    timerRef.current = null;
    pollRef.current = null;
  };

  const resetState = () => {
    clearTimers();
    paidRef.current = false;
    txidRef.current = null;
    idAppRef.current = null;
    setStep('choose');
    setPixLoading(false);
    setPixData(null);
    setPixError('');
    setSecondsLeft(0);
    setExpired(false);
    setConfirming(false);
    setCopied(false);
  };

  // Reinicia o estado sempre que o modal abre.
  useEffect(() => {
    if (show) resetState();
    // Intencional: reagir apenas à mudança de `show`. Disable genérico (sem
    // nomear a regra) porque o ESLint do build de produção pode não carregar o
    // plugin react-hooks, e um nome de regra desconhecido quebra o build.
    // eslint-disable-next-line
  }, [show]);

  // Garante limpeza ao desmontar.
  useEffect(() => clearTimers, []);

  const registerBaixa = async () => {
    const device = { marca: '', modelo: '', so: osName, so_versao: osVersion };
    const bills = selected.map((item, index) => {
      const [d, m, y] = item.date.split('/');
      const descontoCents =
        (discount.perTitleCents && discount.perTitleCents[index]) || 0;
      return {
        cliente: user.name,
        contrato: item.contrato,
        chave: item.chave,
        nsu: '',
        aut: '',
        gateway: '',
        bandeira: 'PIX',
        valor_desconto: Number((descontoCents / 100).toFixed(2)),
        valor_juros: 0,
        valor_multa: 0,
        valor_titulo: toLocaleCurrency(item.price),
        data_baixa: new Date(),
        vencimento: `${y}${m}${d}`,
      };
    });

    const payload = {
      baixas: bills,
      id_app: idAppRef.current,
      contrato: bills[0].contrato,
      cnpj,
      cgc_cliente: user.cpf,
      cliente: user.name,
      mensagem_contrato: '',
      quantidade_parcelas: 1,
      dt_reagendamento: '',
      forma_pagamento: 'PX',
      cobrador: '',
      status: 'realizado',
      dt_agenda: '',
      dt_cobranca: new Date(),
      total: Number(finalTotal.toFixed(2)),
      payment_profile: '',
      mot_reprovador: '',
      origin: 'portal',
      mot_reagendamento: '',
      date_to: '',
      lat: 0,
      lng: 0,
      txid: txidRef.current,
      device,
    };

    await apiCharge.post('/v2/charges/payment/portal/create', payload, {
      headers: { Authorization: 'Bearer ' + token },
    });
  };

  const handlePaid = async () => {
    if (paidRef.current) return;
    paidRef.current = true;
    clearTimers();
    setConfirming(true);
    try {
      // DTGD-1535: este POST é a única chance do front registrar a baixa. Se ele
      // falha, o PIX fica liquidado e o título em aberto. O retry cobre a falha
      // transitória (timeout de 10s do axios, 5xx, rede oscilando no celular);
      // o que escapar daqui é recuperado pelo PixChargePaymentOrfaJob do
      // backend-virtus, que varre transações liquidadas sem baixa.
      await retryAsync(registerBaixa, { attempts: 3, delayMs: 1000 });
    } catch (e) {
      // O PIX foi pago: não bloqueamos o usuário nem escondemos a confirmação.
      // Mas o erro precisa aparecer em algum lugar — antes ele era engolido em
      // silêncio e ninguém ficava sabendo que a baixa não foi gravada.
      console.error(
        '[DTGD-1535] falha ao registrar a baixa do PIX',
        { txid: txidRef.current, idApp: idAppRef.current },
        e,
      );
    } finally {
      setConfirming(false);
      setStep('success');
    }
  };

  const startTimer = seconds => {
    setSecondsLeft(seconds);
    timerRef.current = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearTimers();
          setExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const startPolling = txid => {
    pollRef.current = setInterval(async () => {
      try {
        const status = normalize(await validatePix(txid, token));
        if (status === 'LIQUIDADO') handlePaid();
      } catch (e) {
        // silencioso; tenta de novo no próximo ciclo
      }
    }, 5000);
  };

  const connectSocket = txid => {
    const socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      forceNew: true,
      reconnection: true,
      timeout: 10000,
    });
    socket.on('paymentStatusUpdate', payload => {
      const id = payload && (payload.transactionId || payload.txid || payload.txId);
      if (String(id) === String(txid) && normalize(payload.status) === 'LIQUIDADO') {
        handlePaid();
      }
    });
    socketRef.current = socket;
  };

  const handleGeneratePix = async () => {
    setStep('pix');
    setPixError('');
    setExpired(false);
    setPixData(null);
    setCopied(false);
    paidRef.current = false;
    setPixLoading(true);
    idAppRef.current = CryptoJS.MD5(user.cpf + Date.now()).toString();

    const payload = {
      id_app: idAppRef.current,
      cgc_cliente: user.cpf,
      nome_cliente: user.name,
      cnpj,
      contrato: selected[0].contrato,
      baixas: selected.map((item, index) => ({
        chave: item.chave,
        valor: Number(netValor(index).toFixed(2)),
      })),
      valor: Number(finalTotal.toFixed(2)),
      expiracao: PIX_EXPIRATION,
      pix_type: 'imediato',
      devedor: { inscricao: user.cpf, nome: user.name },
      cobrador: '',
    };

    try {
      const data = await generatePix(payload, token);
      const pix = data.pix || data;
      txidRef.current = pix.txid;
      setPixData(pix);
      startTimer(pix.expiracao || PIX_EXPIRATION);
      startPolling(pix.txid);
      connectSocket(pix.txid);
    } catch (err) {
      setPixError(
        err?.response?.data?.details?.message ||
          err?.response?.data?.message ||
          'Não foi possível gerar o PIX. Tente novamente em instantes.',
      );
    } finally {
      setPixLoading(false);
    }
  };

  const handleClose = () => {
    if (paidRef.current) {
      // PIX já liquidado: avisa o pai para remover os títulos pagos da lista
      // sem precisar recarregar a tela.
      if (onPaid) onPaid(selected.map(item => item.chave));
    } else if (txidRef.current) {
      // DTGD-1535: antes cancelava direto. Como a detecção depende de um polling
      // de 5s, o usuário podia pagar e fechar dentro dessa janela — e o portal
      // cancelava uma cobrança já liquidada, sem nunca registrar a baixa.
      // Revalida primeiro: se liquidou, registra; só cancela o que de fato
      // continua em aberto.
      //
      // Não passa por `handlePaid` de propósito: o modal fecha logo abaixo, e
      // `handlePaid` mexe em estado de componente já desmontado. Aqui só
      // interessa gravar a baixa e avisar o pai.
      const txid = txidRef.current;
      const chaves = selected.map(item => item.chave);
      paidRef.current = true;

      validatePix(txid, token)
        .then(result => {
          if (normalize(result) !== 'LIQUIDADO') {
            paidRef.current = false;
            return cancelPix(
              { contrato: selected[0].contrato, cnpj, cgcCliente: user.cpf },
              token,
            );
          }

          return retryAsync(registerBaixa, { attempts: 3, delayMs: 1000 })
            .then(() => {
              if (onPaid) onPaid(chaves);
            })
            .catch(e => {
              console.error(
                '[DTGD-1535] falha ao registrar a baixa do PIX no fechamento',
                { txid, idApp: idAppRef.current },
                e,
              );
            });
        })
        .catch(() => {
          // Sem resposta do validate não dá para afirmar nada: não cancela (a
          // cobrança pode estar paga) nem registra baixa. O
          // PixChargePaymentOrfaJob resolve se tiver liquidado.
          paidRef.current = false;
        });
    }
    clearTimers();
    onClose();
  };

  const handleSelectCard = () => {
    clearTimers();
    onSelectCard();
  };

  const formatTimer = () => {
    const m = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
    const s = String(secondsLeft % 60).padStart(2, '0');
    return `${m}:${s}`;
  };

  const renderChoose = () => (
    <>
      <span className="pm-method-label">Forma de pagamento</span>
      <div className="pm-methods">
        <button
          type="button"
          className="pm-method-card"
          onClick={handleGeneratePix}
        >
          <span className="pm-method-icon pix">🏦</span>
          <span>
            <span className="pm-method-name">PIX</span>
            <span className="pm-method-desc">Instantâneo</span>
          </span>
        </button>
        <button
          type="button"
          className="pm-method-card"
          onClick={handleSelectCard}
        >
          <span className="pm-method-icon card">💳</span>
          <span>
            <span className="pm-method-name">Cartão de crédito</span>
            <span className="pm-method-desc">Até 12x</span>
          </span>
        </button>
      </div>
    </>
  );

  const renderPix = () => {
    if (pixLoading) {
      return (
        <div className="pm-center">
          <div className="spinner-border text-primary" role="status" />
          <p className="pm-muted">Gerando PIX, aguarde...</p>
        </div>
      );
    }
    if (confirming) {
      return (
        <div className="pm-center">
          <div className="spinner-border text-success" role="status" />
          <p className="pm-muted">Pagamento confirmado! Registrando...</p>
        </div>
      );
    }
    if (pixError) {
      return (
        <>
          <div className="alert alert-danger" role="alert">
            {pixError}
          </div>
          <Button
            variant="primary"
            className="w-100"
            onClick={handleGeneratePix}
          >
            Tentar novamente
          </Button>
        </>
      );
    }
    if (expired) {
      return (
        <>
          <div className="alert alert-warning" role="alert">
            O PIX expirou. Gere um novo código para pagar.
          </div>
          <Button
            variant="primary"
            className="w-100"
            onClick={handleGeneratePix}
          >
            Gerar novo PIX
          </Button>
        </>
      );
    }
    if (!pixData) return null;

    return (
      <div className="pm-pix">
        <p className="pm-pix-label">
          Escaneie o QR Code ou copie o código para pagar
        </p>
        {discount.totalDiscount > 0 && (
          <p className="pm-pix-label" style={{ color: 'var(--brand-primary)' }}>
            Desconto aplicado: R$ {toMoney(discount.totalDiscount)} — total R${' '}
            {toMoney(finalTotal)}
          </p>
        )}
        {pixData.image_qr_code_b64 && (
          <div className="pm-qr">
            <img
              src={pixData.image_qr_code_b64}
              alt="QR Code PIX"
            />
          </div>
        )}
        <div className="pm-timer">⏱ Expira em {formatTimer()}</div>
        <div className="pm-copy-box">
          <code>{pixData.pixCopiaECola}</code>
        </div>
        <CopyToClipboard
          text={(pixData.pixCopiaECola || '').trim()}
          onCopy={() => setCopied(true)}
        >
          <Button variant="primary" size="sm">
            <img
              src={Copybar}
              alt="copiar"
              style={{ width: 16, height: 16, marginRight: 6 }}
            />
            {copied ? 'Copiado!' : 'Copiar código'}
          </Button>
        </CopyToClipboard>
        <p className="pm-waiting">Aguardando o pagamento...</p>
      </div>
    );
  };

  const renderSuccess = () => (
    <div className="pm-success">
      <div className="pm-success-icon">✅</div>
      <div className="pm-success-title">Pagamento realizado!</div>
      <p className="pm-muted">
        Seu pagamento foi processado com sucesso.
      </p>
      <Button variant="primary" onClick={handleClose}>
        Concluir
      </Button>
    </div>
  );

  return (
    <Modal show={show} onHide={handleClose} centered size="md">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '16px', fontWeight: 600 }}>
          Checkout
          {discount.totalDiscount > 0 && (
            <span
              className="pm-total-badge"
              style={{
                textDecoration: 'line-through',
                opacity: 0.6,
                marginRight: 6,
              }}
            >
              R$ {toMoney(total)}
            </span>
          )}
          <span className="pm-total-badge">R$ {toMoney(finalTotal)}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {step === 'choose' && renderChoose()}
        {step === 'pix' && renderPix()}
        {step === 'success' && renderSuccess()}
      </Modal.Body>
    </Modal>
  );
}

export default PaymentModal;
